import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { selectUserToken } from '../../../../../redux/features/user/userSelectors';
import { useGetAllAgentsQuery } from '../../../../../redux/services/agents/agentsApi';
import { useReassignRealtyMutation } from '../../../../../redux/services/realties/realtiesApi';

import { SelectInput } from '../../../inputs/SelectInput';
import { ButtonTemplate } from '../../../../ui/buttons/ButtonTemplate';

import { EMPTY_ARRAY } from '../../../../../data/constants';
import { handleResponseError } from '../../../../../utils/data';
import { showError } from '../../../../../utils/ui';

export const ReassignRealty = ({ realtyId, agent, handleClose = () => {} }) => {
  const [target, setTarget] = useState(null);

  const token = useSelector(selectUserToken);
  const { data: agents = EMPTY_ARRAY, isLoading: isAgentsLoading } =
    useGetAllAgentsQuery(token);
  const [reassignRealty, { isLoading, originalArgs }] =
    useReassignRealtyMutation();
  const isReturning = isLoading && !originalArgs?.agentId;
  const isReassigning = isLoading && !!originalArgs?.agentId;

  const options = useMemo(
    () => agents.filter(item => item.id !== agent?.id),
    [agents, agent?.id]
  );
  const selected = useMemo(() => (target ? [target] : EMPTY_ARRAY), [target]);

  const handleTargetChange = ([item]) => setTarget(item ?? null);

  const submit = async (agentId, message) => {
    const response = await reassignRealty({ token, id: realtyId, agentId });

    if (response.error) {
      showError(handleResponseError(response.error));
      return;
    }

    toast.success(message);
    handleClose();
  };

  return (
    <div className="flex flex-col gap-4 w-[260px] sm:w-[420px] exclude-click">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-blackColor">Listing owner</h2>
        <p className="text-sm text-darkGrayColor">
          Current owner:{' '}
          <span className="font-medium text-blackColor">{agent?.name || '—'}</span>
        </p>
      </div>

      <SelectInput
        options={options}
        initialSelected={selected}
        onSelect={handleTargetChange}
        onRemove={handleTargetChange}
        placeholder={isAgentsLoading ? 'Loading agents…' : 'Reassign to…'}
        isSearchable
        isBoxed
        isDisabled={isAgentsLoading}
      />

      <div className="flex flex-wrap justify-between gap-2">
        <ButtonTemplate
          text="Return to import pool"
          handleClick={() => submit(null, 'Listing returned to the import pool')}
          isLoadingData={isReturning}
          disabled={isLoading}
          isSmall={true}
          isSmallBorder={true}
        />
        <ButtonTemplate
          text="Reassign"
          handleClick={() => submit(target.id, `Listing reassigned to ${target.title}`)}
          isLoadingData={isReassigning}
          disabled={!target || isLoading}
          isSmall={true}
          isSmallBorder={true}
        />
      </div>
    </div>
  );
};
